import React from 'react';
import { motion } from 'framer-motion';
import { Info, Download, FileText, FileDown } from 'lucide-react';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/UI/PageHeader';
import Container from '../components/UI/Container';
import SectionHeader from '../components/UI/SectionHeader';
import Button from '../components/UI/Button';

const ProiectMiauCasa: React.FC = () => {
    return (
        <>
            <SEOHead
                title="Proiect Miau Casa"
                description="Proiectul Miau Casa - o inițiativă dedicată protecției și îngrijirii pisicilor comunitare din Sectorul 1."
                keywords="miau casa, pisici, sector 1, protectia animalelor, sterilizari, hrana pisici"
            />

            <PageHeader
                title="Proiectul Miau Casa"
                subtitle="Protecție și grijă pentru pisicile comunitare"
                image="/cat-outside-proiect-miau.png"
            />

            <Container>
                <SectionHeader
                    title="Despre Proiect"
                    centered={false}
                />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16 items-start">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                            <strong>Ne bucurăm tare mult să fim parte, ca parteneri, din proiectul Miau Casa.</strong>
                        </p>
                        <p className="text-gray-700 mb-6 leading-relaxed">
                            Prin acest proiect, Asociația Sache asigură, în limita posibilităților, sterilizări gratuite pentru coloniile de pisici din Sectorul 1, dar și hrană, prin amplasarea unor automate speciale.
                            Primul dintre ele a fost instalat chiar în Parcul Nicolae Iorga. E un moment care ne bucură enorm.
                        </p>

                        <div className="bg-blue-50 border-l-4 border-primary p-6 italic mb-6 rounded-r-lg">
                            <p className="text-gray-700">
                                "Este, însă, și un început... puțin dificil. Pentru că orice proiect nou vine cu așteptări diferite.
                                Automatul de ieri a fost, din păcate, ușor abuzat de copii care l-au forțat să elibereze mult mai multă hrană decât este gândit să ofere. Poate nu din răutate, ci din lipsă de informație. Din curiozitate. Din joacă."
                            </p>
                        </div>

                        <p className="text-gray-700 mb-6 leading-relaxed">
                            Și tocmai aici e miza reală a acestui proiect. <strong>Miau Casa nu este doar despre pisici.</strong> Este despre comunitate. Despre educație.
                        </p>

                        <p className="text-gray-700 mb-6 leading-relaxed">
                            Despre a învăța, împreună, că grija nu înseamnă exces, că ajutorul are reguli, că binele se face cu măsură și responsabilitate.
                            Automatul nu este o jucărie. Este un instrument de sprijin pentru pisicile comunitare, sprijin real pentru a putea supraviețui, trăi, dar și un instrument de educație pentru noi toți.
                        </p>

                        <p className="font-semibold text-gray-800">
                            Acest proiect nu se construiește peste noapte. Se construiește împreună.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="space-y-6"
                    >
                        {/* Call to Action Card */}
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                            <div className="w-full bg-gray-50 border-b border-gray-100 max-h-[400px] overflow-hidden group">
                                <img
                                    src="/proiect-miau-casa.png"
                                    alt="Proiect Miau Casa"
                                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                                />
                            </div>

                            <div className="p-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-4 font-heading flex items-center">
                                    <Info className="mr-2 text-primary" />
                                    Vrei să te implici?
                                </h3>
                                <p className="text-gray-700 mb-6 leading-relaxed">
                                    Dacă dorești să participi în cadrul acestui proiect și să ajuți la amplasarea căsuțelor pentru pisici, te rugăm să descarci formularul de mai jos, să îl completezi și să ni-l trimiți.
                                </p>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <a
                                            href="/Cerere-Amplasare-Casute-Pisici-Sector1.docx"
                                            download="Cerere_Amplasare_Casute_Pisici_Sector1.docx"
                                            className="flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-dark transition-colors duration-300 shadow-sm"
                                        >
                                            <Download className="w-5 h-5 mr-2" />
                                            Descarcă DOCX
                                        </a>
                                        
                                        <a
                                            href="/Cerere-Amplasare-Casute-Pisici-Sector1.pdf"
                                            download="Cerere_Amplasare_Casute_Pisici_Sector1.pdf"
                                            className="flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-accent hover:bg-accent-dark transition-colors duration-300 shadow-sm"
                                        >
                                            <FileDown className="w-5 h-5 mr-2" />
                                            Descarcă PDF
                                        </a>
                                    </div>

                                    <Button
                                        href="/contact?subject=proiect-miau-casa"
                                        variant="outline"
                                        className="w-full justify-center"
                                    >
                                        <FileText className="w-5 h-5 mr-2" />
                                        Trimite Cererea Completată
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </Container>
        </>
    );
};

export default ProiectMiauCasa;
